import { SessionStorageKeys } from "@hooks/useSessionStorage";

const LogStyle = {
    info: "color: green; font-size: 12px;",
    warn: "color: yellow; font-size: 14px;",
    error: "color: red; font-size: 16px;",
};

/**
 * Used to log breaking/critical errors in the code execution to the console.
 * Should be used when things go very wrong.
 * @param message
 */
export const logError = (message: string | Error, ...data: any[]) => {
    if (process.env.ENABLE_CONSOLE_LOGGING === "True" || location.hostname === "localhost") {
        if (message instanceof Error) {
            console.error(message);
        } else {
            console.error(`%c[ERROR] ${message}`, LogStyle.error);
        }
    }
};

/**
 * Used for logging warnings to the console. These should be non-breaking
 * errors that need a higher level of awareness.
 * @param message Message to be logged
 */
export const logWarn = (message: string) => {
    if (process.env.ENABLE_CONSOLE_LOGGING === "True" || location.hostname === "localhost") {
        console.warn(`%c[WARN] ${message}`, LogStyle.warn);
    }
};

/**
 * Used for logging info to the console to aid debugging.
 * @param message Message to be logged.
 */
export const logInfo = (message: string) => {
    if (process.env.ENABLE_CONSOLE_LOGGING === "True" || location.hostname === "localhost") {
        console.info(`%c[INFO] ${message}`, LogStyle.info);
    }
};

/**
 * Scope of the debugging message. Used to filter out messages.
 */
export const DebugScope = {
    Default: "default",
    Pomodoro: "pomodoro",
} as const;
export type DebugScope = (typeof DebugScope)[keyof typeof DebugScope];

/**
 * Used for logging debugging info to the console.
 * @param scope
 * @param message
 */
export const logDebug = (scope: DebugScope, ...message: unknown[]) => {
    const sessionScope = sessionStorage.getItem(SessionStorageKeys.DebugScope) ?? "";
    if (scope === sessionScope) {
        console.debug(...message);
    }
};
